<script lang="ts">
  import { ScrollArea } from "$lib/components/ui/scroll-area/index.js";
  import { type CustomerSummary } from "$src/domain/customers/CustomerSummary";
  import AddressValue from "$src/presentation/core/address-value.svelte";
  import { t } from "svelte-i18n";
  import * as Pagination from "$lib/components/ui/pagination";
  import { createEventDispatcher, onMount } from "svelte";
  import LoadingIcon from "$src/presentation/defaults/loading-icon.svelte";
  import * as Card from "$lib/components/ui/card";
  import { Button } from "$lib/components/ui/button";
  import { CirclePlus } from "lucide-svelte";
  import { push, querystring } from "svelte-spa-router";
  import TimeSince from "$src/presentation/core/time-since.svelte";
  import { useCustomers } from "$src/presentation/customers/hooks/useCustomers";
  import { StringUtils } from "$lib/stringUtils";
  import { getCustomerInfo } from "$src/domain/customers/utils/customer-info-utils";
  import { getEndIndex, getStartIndex } from "$src/domain/core/utils/paged-result-utils";

  export let groupStoreId: number;
  let currentPage = 1;

  let selectedCustomer: CustomerSummary | undefined;
  let currentSearch: string = "";

  onMount(() => extractQueryString($querystring));

  $: extractQueryString($querystring);

  function extractQueryString(qs?: string) {
    var searchParam = new URLSearchParams(qs);
    if (searchParam) {
      currentPage = StringUtils.getNumberOrDefault(searchParam.get("page"), 1);
      currentSearch = searchParam.get("search") ?? "";
    } else {
      currentPage = 1;
      currentSearch = "";
    }
  }

  const dispatch = createEventDispatcher();
  function selectCustomer(customer: CustomerSummary) {
    selectedCustomer = customer;
    dispatch("customerClick", customer);
  }

  function setPage(page: number) {
    push(`/gs/${groupStoreId}/projects?page=${page}`);
  }

  function newProject() {
    push(`/gs/${groupStoreId}/projects/new`);
  }

  $: queryResult = useCustomers(groupStoreId, currentPage);
</script>

<Card.Root class="flex flex-col w-full flew-grow overflow-hidden">
  <Card.Header>
    <Card.Title class="flex flew-row w-full justify-between">
      <div class="flex-grow">{$t("page.projects.index.customer-title")}</div>
      <Button size="sm" class="h-7 gap-1" on:click={newProject}>
        <CirclePlus class="h-3.5 w-3.5" />
        <span class="sr-only sm:not-sr-only sm:whitespace-nowrap">
          {$t("page.projects.index.newProject")}
        </span>
      </Button>
    </Card.Title>
  </Card.Header>
  {#if $queryResult.status === "loading"}
    <LoadingIcon />
  {:else if $queryResult.isSuccess && $queryResult.data}
    <Card.Content class="flex flew-grow overflow-hidden">
      {#if $queryResult.data.totalCount > 0}
        <ScrollArea orientation="vertical" class="h-full w-full">
          <div class="flex flex-col gap-2 pt-0">
            {#each $queryResult.data.items as customer}
              <button
                class="flex flex-col gap-2 rounded-lg border p-3 text-left text-sm transition-all hover:bg-accent {selectedCustomer?.id ===
                  customer.id && 'bg-muted'}"
                on:click={() => selectCustomer(customer)}
              >
                <div class="flex w-full flex-col gap-1">
                  <div class="flex items-center">
                    <div class="flex items-center gap-2">
                      <div class="font-semibold">
                        {getCustomerInfo(customer)}
                      </div>
                    </div>
                    <div
                      class="ml-auto text-xs {selectedCustomer?.id ===
                      customer.id
                        ? 'text-foreground'
                        : 'text-muted-foreground'}"
                    >
                      <TimeSince dateString={customer.lastProjectDate} />
                    </div>
                  </div>
                </div>
                <div class="line-clamp-2 text-xs text-muted-foreground">
                  <AddressValue value={customer.billingAddress} />
                </div>
              </button>
            {/each}
          </div>
        </ScrollArea>
      {:else}
        {$t("global.messages.noresult")}
      {/if}
    </Card.Content>
    <Card.Footer class="flex">
      <Pagination.Root
        class="flex flex-row justify-end"
        count={$queryResult.data.totalCount}
        perPage={$queryResult.data.pageSize}
        page={currentPage}
        onPageChange={(page) => setPage(page)}
        let:pages
      >
        <Pagination.Content>
          <div class="text-xs">
            {`${getStartIndex($queryResult.data)}-${getEndIndex($queryResult.data)} de ${$queryResult.data.totalCount}`}
          </div>
          <Pagination.Item>
            <Pagination.PrevButton />
          </Pagination.Item>
          <Pagination.Item>
            <Pagination.NextButton />
          </Pagination.Item>
        </Pagination.Content>
      </Pagination.Root>
    </Card.Footer>
  {:else if $queryResult.status === "error"}
    <p style="color: red">{$queryResult.error.message}</p>
  {/if}
</Card.Root>
